import { Box, Card, CardMedia, Divider, Stack, Typography } from '@mui/material';
import CustomSwitch from 'common/components/CustomSwitch';
import CustomTooltip from 'common/components/CustomTooltip';
import useFlexBoxStyles from 'common/flexBox';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

const COVER_IMG = 'https://cdn.onlive.site/assets/images/empty.jpg';

const useStyles = makeStyles()((theme) => {
    const red = 'rgba(255,0,0,0.7)';
    const warning = 'rgba(237, 108, 2, 0.7)';
    return {
        card: {
            overflow: 'hidden',
            minWidth: theme.spacing(18),
        },

        cardMedia: {
            height: theme.spacing(12),
            [theme.breakpoints.up('xl')]: {
                height: theme.spacing(16),
            },
        },

        cardActions: {
            padding: theme.spacing(1),
            zIndex: 2,
        },

        overlay: {
            inset: 0,
            background: 'rgba(0,0,0,0.30)',
            opacity: 0,
            transition: 'opacity 0.3s ease',
            pointerEvents: 'none',
        },

        floatingActions: {
            opacity: 0,
            transform: 'translateY(-10px)',
            transition: 'all 0.25s ease',
            pointerEvents: 'none',
        },

        floatingActionsVisible: {
            transform: 'translateY(0)',
            pointerEvents: 'auto',
        },

        error: {
            border: `2px solid ${red}`,
        },
        warning: {
            border: `2px solid ${warning}`,
        },
    };
});

const ServiceFormCalendarItemCard = ({
    onClick,
    image,
    title,
    titleDivider,
    cardBody,
    actions,
    borderRadius = 0,
    active,
    activeSwitch,
    activeCalendar,
    state,
    enableAdd,
}) => {
    const { t } = useTranslation();
    const { classes, cx } = useStyles();
    const { classes: flexBox } = useFlexBoxStyles();

    const [hovered, setHovered] = useState(false);

    return (
        <Card sx={{ borderRadius }} elevation={4} className={classes.card}>
            <Stack
                className={cx(flexBox.relative, flexBox.h100, !enableAdd && classes[state], flexBox.borderRadius1)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                <div className={cx(classes.overlay, flexBox.absolute, flexBox.zIndex1, hovered && flexBox.opacity1)} />
                <CardMedia
                    component="img"
                    className={cx(classes.cardMedia, !enableAdd && state === 'warning' && flexBox.opacity03)}
                    image={image ?? COVER_IMG}
                    alt={title}
                />

                {activeSwitch && (
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        className={cx(
                            classes.cardActions,
                            classes.floatingActions,
                            hovered && cx(flexBox.opacity1, classes.floatingActionsVisible),
                            flexBox.absolute,
                            flexBox.left0,
                            flexBox.top05,
                            flexBox.zIndex2,
                        )}
                    >
                        <CustomTooltip
                            arrow
                            title={
                                active ? t('services.calendar.inactiveTooltip') : t('services.calendar.activeTooltip')
                            }
                        >
                            <CustomSwitch checked={active} onChange={activeCalendar} name="active" color="primary" />
                        </CustomTooltip>
                    </Stack>
                )}

                {actions && (
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="flex-end"
                        className={cx(
                            classes.cardActions,
                            classes.floatingActions,
                            hovered && cx(flexBox.opacity1, classes.floatingActionsVisible),
                            flexBox.absolute,
                            flexBox.right0,
                            flexBox.zIndex2,
                        )}
                    >
                        {actions}
                    </Stack>
                )}

                <Stack className={flexBox.padding1}>
                    {title && (
                        <CustomTooltip arrow title={title}>
                            <Typography
                                variant="subtitle1"
                                fontWeight={500}
                                component="div"
                                gutterBottom
                                noWrap
                                className={flexBox.w100}
                            >
                                {title}
                            </Typography>
                        </CustomTooltip>
                    )}

                    <Box sx={{ mb: 1 }}>{titleDivider && <Divider />}</Box>

                    {cardBody}
                </Stack>
            </Stack>
        </Card>
    );
};

export default ServiceFormCalendarItemCard;
